import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { ExtractionJob, ProposalFields } from "@shared/modules/x-extraction";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Badge } from "../../../core/components/ui/badge";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { applyJob, getJob, rejectJob } from "../lib/api";

type Job = ExtractionJob & { anonymizedPreview?: string | null };

const LABELS: Record<string, string> = {
  firstName: "Prénom",
  lastName: "Nom",
  sex: "Sexe",
  birthDate: "Date de naissance (AAAA-MM-JJ)",
  nationality: "Nationalité",
  countryResidence: "Pays de résidence",
  city: "Ville",
  email: "Courriel",
  phone: "Téléphone",
  profession: "Profession",
  educationLevel: "Niveau d'études",
  educationDetail: "Études (détail)",
  experienceYears: "Années d'expérience",
  languages: "Langues (virgules)",
  passportNumber: "Numéro de passeport",
  passportExpiresAt: "Expiration du passeport (AAAA-MM-JJ)",
  summary: "Résumé (non enregistré)",
};
const ORDER = Object.keys(LABELS);
const EDUCATION = ["", "aucun", "primaire", "secondaire", "professionnel", "collegial", "universitaire_1", "universitaire_2", "universitaire_3", "autre"];

export function ReviewPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const [job, setJob] = useState<Job | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getJob(id)
      .then((j) => {
        setJob(j);
        const f = j.proposal?.fields ?? {};
        const d: Record<string, string> = {};
        for (const k of ORDER) {
          const v = (f as Record<string, unknown>)[k];
          d[k] = Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v);
        }
        setDraft(d);
      })
      .catch(() => {
        push("danger", "Proposition introuvable");
        navigate("/importations");
      });
  }, [id, navigate, push]);

  if (!job) return <div className="page">Chargement…</div>;
  // Une lecture issue d'un lot d'analyse ramène à son lot.
  const back = job.lotId ? `/analyse/${job.lotId}` : "/importations";

  const fields = (): ProposalFields => {
    const out: Record<string, unknown> = {};
    for (const k of ORDER) {
      if (k === "summary") continue;
      const v = draft[k]?.trim() ?? "";
      if (!v) continue;
      if (k === "experienceYears") out[k] = Number(v);
      else if (k === "languages") out[k] = v.split(",").map((s) => s.trim()).filter(Boolean);
      else out[k] = v;
    }
    return out as ProposalFields;
  };

  const sources = job.proposal?.sources ?? {};

  return (
    <div className="page">
      <PageHeader
        title="Valider la lecture"
        subtitle={job.originalName ?? job.documentId}
        actions={
          <Link className="btn btn--ghost" to={back}>
            Retour
          </Link>
        }
      />
      {job.proposal?.warnings.length ? (
        <p className="review__warnings">
          {job.proposal.warnings.map((w) => (
            <Badge key={w} variant="warning">
              {w.startsWith("modele_indisponible") ? "modèle local indisponible : identité seule" : w.startsWith("modele_en_erreur") ? "le modèle a échoué" : w === "texte_insuffisant" ? "peu de texte lisible" : w}
            </Badge>
          ))}
        </p>
      ) : null}

      <div className="review">
        <Card title="Champs proposés" subtitle="Corrigez ce qui doit l'être. Rien n'est enregistré avant « Appliquer ».">
          <div className="form-grid">
            {ORDER.map((k) => (
              <Field key={k} label={LABELS[k]!} hint={sources[k] === "identite" ? "détecté dans le document" : sources[k] === "modele" ? "proposé par le modèle local" : undefined}>
                {k === "summary" ? (
                  <Textarea rows={3} value={draft[k] ?? ""} readOnly />
                ) : k === "educationLevel" ? (
                  <Select value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}>
                    {EDUCATION.map((o) => (
                      <option key={o} value={o}>
                        {o || "—"}
                      </option>
                    ))}
                  </Select>
                ) : k === "sex" ? (
                  <Select value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}>
                    <option value="">—</option>
                    <option value="F">Femme</option>
                    <option value="M">Homme</option>
                    <option value="X">Autre</option>
                  </Select>
                ) : (
                  <Input value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
                )}
              </Field>
            ))}
          </div>
          <div className="review__actions">
            <Button
              variant="ghost-danger"
              disabled={busy || job.status !== "a_valider"}
              onClick={async () => {
                setBusy(true);
                try {
                  await rejectJob(job.id);
                  push("info", "Proposition rejetée");
                  navigate(back);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Rejeter
            </Button>
            <Button
              variant="primary"
              disabled={busy || job.status !== "a_valider"}
              onClick={async () => {
                setBusy(true);
                try {
                  await applyJob(job.id, { fields: fields(), candidateId: job.candidateId });
                  push("success", job.candidateId ? "Fiche mise à jour" : "Dossier créé à partir du CV");
                  navigate(job.candidateId ? `/candidats/${job.candidateId}` : "/candidats");
                } catch {
                  push("danger", "Application impossible");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {job.candidateId ? "Appliquer à la fiche" : "Créer le dossier"}
            </Button>
          </div>
        </Card>

        <Card title="Texte lu (obfusqué)" subtitle="C'est ce texte, et lui seul, que le modèle local a vu.">
          <pre className="review__preview">{job.anonymizedPreview ?? "—"}</pre>
        </Card>
      </div>
    </div>
  );
}
